/**
 * /admin/rules — Rule Library Browser (PRP-002).
 *
 * Fetches the 138 (or however many) active rules, lets the user filter,
 * search, group, and inspect each one. Filter + search state are mirrored
 * to URL query params so views can be deep-linked.
 *
 * Role gating: visible to editor / expert / reviewer / admin / super_admin.
 * Estimators and viewers get redirected to "/" (the sidebar also hides the
 * link, the API also 403s — three layers).
 */
import * as React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { ShieldAlert, Search, Plus, Sparkles } from 'lucide-react';

import useAuthStore from '../stores/authStore';
import { RulesAPI } from '../lib/rulesApi';
import { cn, getErrorMessage } from '../lib/utils';
import { useClickOutside } from '../hooks/useClickOutside';
import RuleCard from '../components/rules/RuleCard';
import RuleDetail from '../components/rules/RuleDetail';
import RuleAuthoringModal from '../components/rules/RuleAuthoringModal';
import RuleFilterSidebar, {
  DEFAULT_FILTER,
  type FilterState,
} from '../components/rules/RuleFilterSidebar';
import { RULE_TYPE_LABELS } from '../components/rules/RuleBadges';
import type {
  GroupByDimension,
  RuleListItem,
  RuleSeverity,
  RuleStatus,
  RuleType,
} from '../types/rules';

const ALLOWED_ROLES = ['editor', 'expert', 'reviewer', 'admin', 'super_admin'];

const SEVERITY_ORDER: Record<RuleSeverity, number> = {
  must: 0,
  should: 1,
  suggested: 2,
};

// ---------------------------------------------------------------------------
// URL <-> state plumbing
// ---------------------------------------------------------------------------

function splitParam(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function joinParam(values: string[]): string | undefined {
  return values.length === 0 ? undefined : values.join(',');
}

function readStateFromUrl(params: URLSearchParams): {
  search: string;
  groupBy: GroupByDimension;
  filter: FilterState;
} {
  const statuses = splitParam(params.get('status')) as RuleStatus[];
  const severities = splitParam(params.get('severity')) as RuleSeverity[];
  const ruleTypes = splitParam(params.get('type')) as RuleType[];
  return {
    search: params.get('q') || '',
    groupBy: (params.get('group') as GroupByDimension) || 'rule_type',
    filter: {
      statuses: statuses.length > 0 ? statuses : DEFAULT_FILTER.statuses,
      severities,
      ruleTypes,
      domains: splitParam(params.get('domains')),
      tags: splitParam(params.get('tags')),
    },
  };
}

function writeStateToUrl(
  setParams: (_next: URLSearchParams) => void,
  search: string,
  groupBy: GroupByDimension,
  filter: FilterState,
) {
  const next = new URLSearchParams();
  if (search) next.set('q', search);
  if (groupBy !== 'rule_type') next.set('group', groupBy);
  // Status: only persist if non-default (i.e. anything other than ["active"]).
  const isDefaultStatus =
    filter.statuses.length === 1 && filter.statuses[0] === 'active';
  if (!isDefaultStatus) {
    const v = joinParam(filter.statuses);
    if (v) next.set('status', v);
    else next.set('status', ''); // explicit "all cleared"
  }
  for (const [k, vs] of [
    ['severity', filter.severities],
    ['type', filter.ruleTypes],
    ['domains', filter.domains],
    ['tags', filter.tags],
  ] as const) {
    const v = joinParam(vs);
    if (v) next.set(k, v);
  }
  setParams(next);
}

// ---------------------------------------------------------------------------
// Filtering + grouping
// ---------------------------------------------------------------------------

function applyFilters(
  rules: RuleListItem[],
  filter: FilterState,
  search: string,
): RuleListItem[] {
  const q = search.trim().toLowerCase();
  return rules.filter((r) => {
    if (filter.statuses.length > 0 && !filter.statuses.includes(r.status)) return false;
    if (filter.severities.length > 0 && !filter.severities.includes(r.severity)) return false;
    if (filter.ruleTypes.length > 0 && !filter.ruleTypes.includes(r.rule_type)) return false;
    if (filter.domains.length > 0 && !filter.domains.some((d) => r.domains.includes(d))) return false;
    if (filter.tags.length > 0 && !filter.tags.some((t) => r.tags.includes(t))) return false;
    if (q) {
      const haystack = `${r.name} ${r.description} ${r.tags.join(' ')}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

interface RuleGroup {
  key: string;
  label: string;
  rules: RuleListItem[];
}

function groupRules(
  rules: RuleListItem[],
  dimension: GroupByDimension,
): RuleGroup[] {
  const map = new Map<string, RuleListItem[]>();
  for (const r of rules) {
    let keys: string[];
    switch (dimension) {
      case 'category':
        keys = [r.category || '(uncategorized)'];
        break;
      case 'domain':
        keys = r.domains.length > 0 ? r.domains : ['(no domain)'];
        break;
      case 'severity':
        keys = [r.severity];
        break;
      case 'rule_type':
      default:
        keys = [r.rule_type];
        break;
    }
    for (const k of keys) {
      const bucket = map.get(k) ?? [];
      bucket.push(r);
      map.set(k, bucket);
    }
  }
  const groups: RuleGroup[] = Array.from(map.entries()).map(([key, list]) => ({
    key,
    label: groupLabel(dimension, key),
    rules: list,
  }));
  groups.sort((a, b) => {
    if (dimension === 'severity') {
      return (
        (SEVERITY_ORDER[a.key as RuleSeverity] ?? 99) -
        (SEVERITY_ORDER[b.key as RuleSeverity] ?? 99)
      );
    }
    return a.label.localeCompare(b.label);
  });
  for (const g of groups) {
    g.rules.sort((a, b) => a.name.localeCompare(b.name));
  }
  return groups;
}

function groupLabel(dimension: GroupByDimension, key: string): string {
  if (dimension === 'rule_type') return RULE_TYPE_LABELS[key as RuleType] || key;
  if (dimension === 'severity') return key.charAt(0).toUpperCase() + key.slice(1);
  return key;
}

// ---------------------------------------------------------------------------
// Debounce hook (for search input)
// ---------------------------------------------------------------------------

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const GROUP_BY_OPTIONS: Array<{ value: GroupByDimension; label: string }> = [
  { value: 'rule_type', label: 'Rule Type' },
  { value: 'category', label: 'Category' },
  { value: 'domain', label: 'Domain' },
  { value: 'severity', label: 'Severity' },
];

export default function RulesPage(): React.ReactElement {
  const { roles } = useAuthStore();
  const hasAccess = ALLOWED_ROLES.some((r) => roles.includes(r));

  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = React.useMemo(
    () => readStateFromUrl(searchParams),
    [searchParams],
  );

  const [searchInput, setSearchInput] = React.useState(urlState.search);
  const debouncedSearch = useDebouncedValue(searchInput, 200);

  const [groupBy, setGroupBy] = React.useState<GroupByDimension>(urlState.groupBy);
  const [filter, setFilter] = React.useState<FilterState>(urlState.filter);

  const [rules, setRules] = React.useState<RuleListItem[] | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [activeRule, setActiveRule] = React.useState<RuleListItem | null>(null);
  const [showAddMenu, setShowAddMenu] = React.useState(false);
  const [authoringOpen, setAuthoringOpen] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);
  const canAuthor = ['admin', 'super_admin'].some((r) => roles.includes(r));

  // Dismiss the "Add new rule" menu on outside click / Escape.
  const addMenuRef = React.useRef<HTMLDivElement>(null);
  useClickOutside(addMenuRef, () => setShowAddMenu(false), showAddMenu);

  // Persist filter/search/group changes back to URL.
  React.useEffect(() => {
    writeStateToUrl(
      (next) => setSearchParams(next, { replace: true }),
      debouncedSearch,
      groupBy,
      filter,
    );
  }, [debouncedSearch, groupBy, filter, setSearchParams]);

  // Fetch all rules on mount + whenever a new rule is authored (reloadKey).
  React.useEffect(() => {
    if (!hasAccess) return;
    let cancelled = false;
    RulesAPI.list()
      .then((list) => {
        if (!cancelled) setRules(list);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(getErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [hasAccess, reloadKey]);

  const filtered = React.useMemo(
    () => (rules ? applyFilters(rules, filter, debouncedSearch) : []),
    [rules, filter, debouncedSearch],
  );

  const groups = React.useMemo(
    () => groupRules(filtered, groupBy),
    [filtered, groupBy],
  );

  // Headline counts: "138 rules — 126 trigger, 12 procedural".
  const headlineCounts = React.useMemo(() => {
    if (!rules) return null;
    const total = rules.length;
    const byType = new Map<RuleType, number>();
    for (const r of rules) byType.set(r.rule_type, (byType.get(r.rule_type) ?? 0) + 1);
    const parts: string[] = [`${total} rule${total === 1 ? '' : 's'}`];
    const breakdown = Array.from(byType.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([type, n]) => `${n} ${RULE_TYPE_LABELS[type]?.toLowerCase() || type}`);
    return parts.concat(breakdown).join(' — ');
  }, [rules]);

  if (!hasAccess) {
    return (
      <div className="mx-auto max-w-md rounded-lg border bg-card p-6 text-center shadow-sm">
        <ShieldAlert className="mx-auto h-10 w-10 text-amber-500 dark:text-amber-400" />
        <h1 className="mt-3 text-lg font-semibold">Editor access required</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The Rule Library is visible to editors, experts, reviewers, and admins.
          Ask a super admin to grant you a role if you need access.
        </p>
        <Navigate to="/" replace />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Rule Library</h1>
          <p className="text-sm text-muted-foreground">
            {loadError
              ? 'Unable to load rules.'
              : headlineCounts || 'Loading rule counts…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canAuthor && (
            <div className="relative" ref={addMenuRef}>
              <button
                type="button"
                onClick={() => setShowAddMenu((v) => !v)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Add new rule
              </button>
              {showAddMenu && (
                <div className="absolute right-0 z-10 mt-1 w-72 rounded-md border bg-card shadow-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMenu(false);
                      setAuthoringOpen(true);
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-muted border-b"
                  >
                    <Sparkles className="h-4 w-4 mt-0.5 text-primary" />
                    <div>
                      <div className="font-medium">Describe in plain language</div>
                      <div className="text-xs text-slate-500 dark:text-muted-foreground">
                        Recommended — LLM-assisted authoring
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddMenu(false);
                      // PRP-004's structured editor (RuleEditForm) edits existing
                      // rules from the detail panel. To create a new rule via
                      // the structured path, generate one with NL authoring (it
                      // saves as draft), then open it and click Edit.
                      alert(
                        'The structured editor (PRP-004) refines existing rules. ' +
                          'To create a new rule: use "Describe in plain language" ' +
                          'first — generated rules save as draft. Then open the ' +
                          'rule and click Edit to refine the JSON directly.',
                      );
                    }}
                    className="flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 dark:hover:bg-muted"
                  >
                    <Plus className="h-4 w-4 mt-0.5 text-slate-500 dark:text-muted-foreground" />
                    <div>
                      <div className="font-medium text-muted-foreground">
                        Use the structured editor
                      </div>
                      <div className="text-xs text-slate-500 dark:text-muted-foreground">
                        For refining existing rules
                      </div>
                    </div>
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, description, tags…"
              className="h-10 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 md:w-72"
            />
          </div>
          <div className="flex items-center gap-1 rounded-md border bg-card p-0.5 shadow-sm">
            <span className="px-2 text-xs text-muted-foreground">Group by</span>
            {GROUP_BY_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setGroupBy(opt.value)}
                className={cn(
                  'rounded px-2 py-1 text-xs font-medium transition',
                  groupBy === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-slate-600 dark:text-muted-foreground hover:bg-muted',
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {loadError && (
        <div className="rounded border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/15 p-4 text-sm text-red-800 dark:text-red-300">
          {loadError}
        </div>
      )}

      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <RuleFilterSidebar
          rules={rules || []}
          value={filter}
          onChange={setFilter}
          onClear={() => setFilter(DEFAULT_FILTER)}
        />

        <div className="min-w-0 flex-1 space-y-6">
          {!rules && !loadError && <ListSkeleton />}
          {rules && filtered.length === 0 && (
            <EmptyState onClear={() => setFilter(DEFAULT_FILTER)} />
          )}
          {rules &&
            filtered.length > 0 &&
            groups.map((group) => (
              <section key={group.key}>
                <header className="mb-2 flex items-baseline justify-between border-b pb-1">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                    {group.label}
                  </h2>
                  <span className="text-xs text-muted-foreground">
                    {group.rules.length} rule{group.rules.length === 1 ? '' : 's'}
                  </span>
                </header>
                <div className="space-y-2">
                  {group.rules.map((r) => (
                    <RuleCard key={r.id} rule={r} onClick={setActiveRule} />
                  ))}
                </div>
              </section>
            ))}
        </div>
      </div>

      <RuleDetail
        rule={activeRule}
        onClose={() => setActiveRule(null)}
        onUpdated={(next) => {
          setRules((prev) =>
            prev
              ? prev.map((r) =>
                  r.id === next.id
                    ? {
                        ...r,
                        name: next.name,
                        description: next.description,
                        severity: next.severity,
                        status: next.status,
                        domains: next.domains,
                        tags: next.tags,
                        rule_type: next.rule_type,
                        citations: next.citations,
                      }
                    : r,
                )
              : prev,
          );
          // Keep the drawer pointed at the same rule but mirror the new
          // summary fields so the header chips update without a refetch.
          setActiveRule((r) =>
            r && r.id === next.id
              ? {
                  ...r,
                  name: next.name,
                  description: next.description,
                  severity: next.severity,
                  status: next.status,
                  rule_type: next.rule_type,
                  domains: next.domains,
                  tags: next.tags,
                }
              : r,
          );
        }}
      />

      <RuleAuthoringModal
        open={authoringOpen}
        onClose={() => setAuthoringOpen(false)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}

const ListSkeleton: React.FC = () => (
  <div className="space-y-3" aria-label="Loading rules">
    {Array.from({ length: 6 }).map((_, i) => (
      <div
        key={i}
        className="h-20 animate-pulse rounded-lg border bg-card shadow-sm"
      />
    ))}
  </div>
);

const EmptyState: React.FC<{ onClear: () => void }> = ({ onClear }) => (
  <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
    <p className="mb-2 font-medium text-muted-foreground">No rules match your filters.</p>
    <p>Try widening severity or status, or remove a search term.</p>
    <button
      type="button"
      onClick={onClear}
      className="mt-3 inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
    >
      Clear filters
    </button>
  </div>
);
